import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container, Card, Button, Alert, Badge, Table,
  Spinner, Modal
} from 'react-bootstrap';
import { db, CompactSession } from '../db';
import { syncPendingSessions } from '../services/syncService';
import { promoteCompactSession } from '../services/compactSessionService';

interface SessionRow {
  session: CompactSession;
  status: 'orphaned_complete' | 'abandoned_incomplete' | 'in_progress';
  recordCount: number;
  alreadyPromoted: boolean;
}

const OrphanRecoveryDashboard: React.FC = () => {
  const { t } = useTranslation();

  const statusLabel = (s: SessionRow['status']) => {
    switch (s) {
      case 'orphaned_complete':
        return <Badge bg="danger">{t('orphanRecovery.finishedNotSynced')}</Badge>;
      case 'abandoned_incomplete':
        return <Badge bg="warning" text="dark">{t('orphanRecovery.incompleteAbandoned2')}</Badge>;
      case 'in_progress':
        return <Badge bg="secondary">{t('orphanRecovery.inProgress')}</Badge>;
    }
  };

  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);
  const [recovering, setRecovering] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<CompactSession | null>(null);

  const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const allCompact = await db.compactSessions.toArray();
      const allScouting = await db.scoutingSessions.toArray();

      const classified: SessionRow[] = allCompact.map(cs => {
        const recordCount = Object.keys(cs.records || {}).length;
        const lastUpdatedDate = (cs.lastUpdated || cs.startTime || '').slice(0, 10);
        const isToday = lastUpdatedDate === todayStr;

        let status: SessionRow['status'];
        if (cs.isComplete === 1) {
          status = 'orphaned_complete';
        } else if (!isToday) {
          status = 'abandoned_incomplete';
        } else {
          status = 'in_progress';
        }

        // Check if there's already a matching scoutingSession for this compactSession
        // We match on orchardId + scouterName + same date (same day as lastUpdated)
        const alreadyPromoted = allScouting.some(ss =>
          ss.metadata?.orchardId === cs.orchardId &&
          ss.scouterName === cs.scouterName &&
          ss.date?.slice(0, 10) === lastUpdatedDate
        );

        return { session: cs, status, recordCount, alreadyPromoted };
      });

      // Sort: orphaned_complete first, then abandoned, then in_progress
      classified.sort((a, b) => {
        const order = { orphaned_complete: 0, abandoned_incomplete: 1, in_progress: 2 };
        return order[a.status] - order[b.status];
      });

      setRows(classified);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setMessage({ type: 'danger', text: 'Failed to load sessions from local database.' });
    } finally {
      setLoading(false);
    }
  }, [todayStr]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const downloadRawJson = (cs: CompactSession) => {
    const blob = new Blob([JSON.stringify(cs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compact-session-${cs.orchardName}-${cs.scouterName}-${(cs.lastUpdated || cs.startTime || '').slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAllJson = async () => {
    const allCompact = await db.compactSessions.toArray();
    const allScouting = await db.scoutingSessions
      .where('syncStatus').anyOf(['pending', 'failed'])
      .toArray();
    const payload = { compactSessions: allCompact, unsynced_scoutingSessions: allScouting, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `citrus-synch-recovery-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const promoteSession = async (cs: CompactSession) => {
    setRecovering(cs.id!);
    setMessage(null);
    try {
      await promoteCompactSession(cs);
      syncPendingSessions();
      setMessage({ type: 'success', text: `Session for "${cs.orchardName}" queued for sync successfully.` });
      await loadSessions();
    } catch (err) {
      console.error('Failed to promote session:', err);
      setMessage({ type: 'danger', text: `Failed to recover session: ${err}` });
    } finally {
      setRecovering(null);
    }
  };

  const deleteSession = async (cs: CompactSession) => {
    try {
      // Also clean up any orphaned media
      const photoIds = Object.values(cs.records || {}).flatMap(r => (r as any).photoIds || []);
      if (photoIds.length > 0) {
        await db.media.where('id').anyOf(photoIds).delete();
      }
      await db.compactSessions.delete(cs.id!);
      setShowDeleteConfirm(null);
      setMessage({ type: 'info', text: `Session for "${cs.orchardName}" deleted.` });
      await loadSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setMessage({ type: 'danger', text: 'Failed to delete session.' });
    }
  };

  const orphanedCount = rows.filter(r => r.status === 'orphaned_complete' && !r.alreadyPromoted).length;
  const abandonedCount = rows.filter(r => r.status === 'abandoned_incomplete').length;

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h3>{t('orphanRecovery.heading')}</h3>
          <p className="text-muted small mb-0">
            {t('orphanRecovery.help')}
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={downloadAllJson}>
          <i className="bi bi-download me-1"></i>{t('orphanRecovery.exportAll')}
        </Button>
      </div>

      {(orphanedCount > 0 || abandonedCount > 0) && (
        <Alert variant="warning" className="mb-3">
          <strong>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {t('orphanRecovery.unsyncedAlert', { count: orphanedCount + abandonedCount })}
          </strong>
          {orphanedCount > 0 && (
            <div className="mt-1 small">
              {t('orphanRecovery.finishedNotUploaded', { count: orphanedCount })}
            </div>
          )}
          {abandonedCount > 0 && (
            <div className="mt-1 small">
              {t('orphanRecovery.incompleteAbandoned', { count: abandonedCount })}
            </div>
          )}
        </Alert>
      )}

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2 text-muted">{t('orphanRecovery.loading')}</p>
        </div>
      ) : rows.length === 0 ? (
        <Card className="text-center py-5 text-muted">
          <Card.Body>
            <i className="bi bi-check-circle-fill text-success fs-2 mb-2 d-block"></i>
            {t('orphanRecovery.noSessions')}
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Table responsive hover className="mb-0 small">
            <thead className="table-dark">
              <tr>
                <th>{t('orphanRecovery.orchardHeader')}</th>
                <th>{t('orphanRecovery.scouterHeader')}</th>
                <th>{t('orphanRecovery.lastUpdatedHeader')}</th>
                <th className="text-center">{t('orphanRecovery.recordsHeader')}</th>
                <th>{t('orphanRecovery.statusHeader')}</th>
                <th>{t('orphanRecovery.actionsHeader')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const cs = row.session;
                const dateStr = (cs.lastUpdated || cs.startTime || '').slice(0, 16).replace('T', ' ');
                const isRecovering = recovering === cs.id;

                return (
                  <tr key={cs.id} className={row.status === 'orphaned_complete' && !row.alreadyPromoted ? 'table-danger' : ''}>
                    <td className="fw-semibold">{cs.orchardName || cs.orchardId}</td>
                    <td>{cs.scouterName}</td>
                    <td className="text-muted">{dateStr}</td>
                    <td className="text-center">
                      <Badge bg={row.recordCount >= 100 ? 'success' : 'secondary'}>
                        {row.recordCount}
                      </Badge>
                    </td>
                    <td>
                      {statusLabel(row.status)}
                      {row.alreadyPromoted && (
                        <Badge bg="success" className="ms-1">{t('orphanRecovery.promoted')}</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {!row.alreadyPromoted && (row.status === 'orphaned_complete' || row.status === 'abandoned_incomplete') && (
                          <Button
                            size="sm"
                            variant="success"
                            disabled={isRecovering || row.recordCount === 0}
                            onClick={() => promoteSession(cs)}
                            title={row.recordCount === 0 ? 'No records to recover' : 'Promote to sync queue'}
                          >
                            {isRecovering ? (
                              <><Spinner size="sm" animation="border" className="me-1" />{t('orphanRecovery.recovering')}</>
                            ) : (
                              t('orphanRecovery.recoverSync')
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => downloadRawJson(cs)}
                          title="Download raw JSON backup of this session"
                        >
                          <i className="bi bi-download"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setShowDeleteConfirm(cs)}
                          title="Delete this local session"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Delete confirmation modal */}
      <Modal show={!!showDeleteConfirm} onHide={() => setShowDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('orphanRecovery.deleteTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t('orphanRecovery.deleteBody', { name: showDeleteConfirm?.orchardName, count: Object.keys(showDeleteConfirm?.records || {}).length })}
          </p>
          <Alert variant="warning" className="mb-0 small">
            {t('orphanRecovery.deleteWarning')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(null)}>{t('orphanRecovery.cancel')}</Button>
          <Button variant="danger" onClick={() => showDeleteConfirm && deleteSession(showDeleteConfirm)}>
            {t('orphanRecovery.delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrphanRecoveryDashboard;
